import {mapChatMessagesToApiHistory} from '../../aiMessageGenerator/utils/types';
import {getAiAgentContext, sendAiAgentMessage} from '../../../redux/aiAgent/aiAgentActions';
import {resolveChatLang} from './detectChatLang';
import {resolveAttachmentUpload} from './resolveAttachmentUpload';
import {rememberTurnContext} from './rememberTurnContext';
import {rememberDiscriminatorFromAsk} from './agentSessionMemory';
import {clearHeldProposal, holdProposal, isProposalConfirmPrompt} from './agentProposalHold';
import {getAgentChatSession, isAgentChatSessionOpen} from './agentChatSession';
import {confirmHeldProposal, runConfirmedProposal} from './confirmProposal';
import type {AiAgentConfirmOptions} from './confirmProposal';
import type {NavigateFn} from './navigateAfterExecute';
import type {AiAgentSubmitHandler} from '../types';

type AiAgentSubmitOptions = AiAgentConfirmOptions & {
    fileTooLarge: string;
    fileUnsupported: string;
};

export function createAiAgentSubmitHandler(
    navigate: NavigateFn,
    options: AiAgentSubmitOptions,
): AiAgentSubmitHandler {
    return async (prompt, history, sessionId, context) => {
        const turnSession = getAgentChatSession();
        const trimmedPrompt = prompt.trim();
        if(isProposalConfirmPrompt(trimmedPrompt)) {
            return confirmHeldProposal(sessionId, navigate, options);
        }
        // Anything else answers the summary with a change, so the held proposal is stale —
        // the server re-proposes with the change, and that turn holds the new one.
        clearHeldProposal();
        rememberDiscriminatorFromAsk(trimmedPrompt, context.pendingAsk);

        const {
            eid, pid, scope, currentRoute, formId, eventType, sessionIntent, currentFormValues, recordCreated,
        } = getAiAgentContext();
        if(!eid && !pid) {
            return null;
        }

        const {imageDataUrl, fileDataUrl, fileName, error: attachmentError} = await resolveAttachmentUpload(context.attachment);
        if(attachmentError) {
            return {text: attachmentError === 'tooLarge' ? options.fileTooLarge : options.fileUnsupported};
        }
        if(!trimmedPrompt && !imageDataUrl && !fileDataUrl) {
            return null;
        }

        const agentPayload = await sendAiAgentMessage({
            prompt: trimmedPrompt,
            history: mapChatMessagesToApiHistory(history),
            sessionId,
            scope,
            currentRoute,
            eid,
            pid,
            formId,
            eventType,
            sessionIntent,
            chatLang: resolveChatLang(trimmedPrompt, context.pendingAsk),
            currentFormValues,
            recordCreated,
            imageDataUrl,
            fileDataUrl,
            fileName,
            pendingAsk: context.pendingAsk
                ? {question: context.pendingAsk.question, options: context.pendingAsk.options}
                : null,
        });

        if(!agentPayload) {
            return null;
        }
        // The producer opened a new chat while this turn was running. Its answer belongs to the
        // chat that was left — remembering any of it here is what makes the fresh chat start
        // inside the previous flow.
        if(!isAgentChatSessionOpen(turnSession)) {
            return null;
        }

        rememberDiscriminatorFromAsk(agentPayload.answeredOption?.value, context.pendingAsk);
        rememberTurnContext(agentPayload);

        // They agreed to the summary in words instead of pressing. The agreement is the
        // confirmation, so the record is created here — otherwise the chat answers "yes" with
        // the same summary again, and the flow has no way to end.
        if(agentPayload.pendingProposal?.confirmed) {
            return runConfirmedProposal(agentPayload.pendingProposal, {scope, eid, pid}, sessionId, navigate, options);
        }
        if(agentPayload.pendingProposal) {
            // The turn only holds the proposal — a summary awaiting confirmation, or an early
            // offer sent while optional fields are still being collected. The owner is stamped
            // now: the confirmation may come after the producer has navigated elsewhere.
            holdProposal(agentPayload.pendingProposal, {scope, eid, pid});
        }
        return {
            text: agentPayload.text || '',
            ...(agentPayload.ask ? {ask: agentPayload.ask} : {}),
        };
    };
}
